import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cpu, Scale, ShieldAlert, BarChart3, Eye, Filter, ScanSearch, Compass, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal, SectionLabel, PrimaryLink, GhostLink } from "@/components/shared";
import { categoryShort, importanceLabel, importanceClass, formatDate } from "@/lib/content";

const fields = [
  { icon: Cpu, title: "Intelligence artificielle, automatisation et outils numériques", text: "Usages concrets de l'intelligence artificielle, automatisation, outils numériques, souveraineté des données et conditions d'une adoption maîtrisée." },
  { icon: Scale, title: "Réglementations, données et facturation électronique", text: "Décryptage accessible des évolutions réglementaires, des obligations nouvelles, de la donnée et de la facturation électronique." },
  { icon: ShieldAlert, title: "Cybersécurité et nouvelles menaces", text: "Vigilance sur les risques numériques, la protection des systèmes et les nouvelles menaces susceptibles d'affecter les PME." },
  { icon: BarChart3, title: "Organisation, compétences, données et pilotage", text: "Évolution des organisations, des compétences, de la donnée et des outils de pilotage au service de la décision." },
];

const method = [
  { icon: Eye, title: "Observer", n: "01" },
  { icon: Filter, title: "Sélectionner", n: "02" },
  { icon: ScanSearch, title: "Décrypter", n: "03" },
  { icon: Compass, title: "Mettre en perspective", n: "04" },
];

export default function Home() {
  const [recent, setRecent] = useState([]);
  const [status, setStatus] = useState("loading");

  const loadRecent = () => {
    setStatus("loading");
    api
      .get("/articles/recent?limit=3")
      .then(({ data }) => {
        setRecent(data);
        setStatus("ok");
      })
      .catch(() => setStatus("error"));
  };

  useEffect(() => {
    loadRecent();
  }, []);

  return (
    <>
      <Seo
        title="BENZENATI — Veille, décryptage et intelligence appliquée aux PME"
        description="Atelier indépendant de veille, de décryptage et de mise en perspective pour les dirigeants de PME. Comprendre les transformations économiques, réglementaires et technologiques pour mieux décider."
        path="/"
      />

      {/* HERO */}
      <section className="border-b border-bz-line" data-testid="home-hero">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-36">
          <Reveal><span className="font-serif text-lg tracking-[0.4em] text-bz-slate">BENZENATI</span></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-8 max-w-5xl font-serif text-4xl leading-[1.03] tracking-tight text-bz-navy sm:text-5xl lg:text-7xl">
              Veille, décryptage et intelligence appliquée aux PME
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-2xl text-lg leading-relaxed text-bz-slate lg:text-xl">
              Comprendre les transformations économiques, réglementaires et technologiques pour mieux décider.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-12 flex flex-col gap-4 sm:flex-row">
              <PrimaryLink to="/veille" testid="hero-cta-decryptages">Découvrir les décryptages</PrimaryLink>
              <GhostLink to="/a-propos" testid="hero-cta-demarche">Comprendre notre démarche</GhostLink>
            </div>
          </Reveal>
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="border-b border-bz-line" data-testid="home-introduction">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-24 lg:grid-cols-12 lg:px-10 lg:py-32">
          <div className="lg:col-span-4">
            <Reveal>
              <SectionLabel>L'Atelier</SectionLabel>
              <p className="mt-8 font-serif text-2xl leading-snug text-bz-navy lg:text-3xl">Un espace indépendant pour prendre du recul.</p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <p className="text-lg leading-relaxed text-bz-slate">
                Les dirigeants de PME évoluent dans un environnement où les réglementations, les technologies, les usages et les modèles économiques changent rapidement. L'information est abondante, mais le temps nécessaire pour la sélectionner, la vérifier et la mettre en perspective manque souvent.
              </p>
              <p className="mt-6 text-lg leading-relaxed text-bz-slate">
                BENZENATI propose un espace indépendant pour prendre du recul, comprendre les évolutions utiles et identifier leurs conséquences possibles pour l'entreprise.
              </p>
              <p className="mt-8 border-l-2 border-bz-amber pl-6 font-serif text-xl leading-snug text-bz-navy" data-testid="home-trust">
                Une veille indépendante, fondée sur des sources officielles et reconnues, traduite en conséquences concrètes pour les PME.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* QUATRE CHAMPS D'OBSERVATION */}
      <section className="border-b border-bz-line bg-bz-ivory2" data-testid="home-fields">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal><SectionLabel>Quatre champs d'observation</SectionLabel></Reveal>
          <div className="mt-16 grid grid-cols-1 gap-px overflow-hidden border border-bz-line bg-bz-line md:grid-cols-2">
            {fields.map((f, i) => (
              <Reveal key={f.title} delay={(i % 2) * 0.1} className="bg-white">
                <div className="flex h-full flex-col p-10 lg:p-12" data-testid={`field-card-${i}`}>
                  <f.icon size={30} strokeWidth={1.2} className="text-bz-petrol" />
                  <h3 className="mt-8 font-serif text-2xl leading-snug text-bz-navy">{f.title}</h3>
                  <p className="mt-5 text-base leading-relaxed text-bz-slate">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DERNIERS SIGNAUX UTILES */}
      <section className="border-b border-bz-line" data-testid="home-signals">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <Reveal>
              <SectionLabel>Derniers signaux utiles</SectionLabel>
              <h2 className="mt-8 max-w-2xl font-serif text-3xl leading-tight text-bz-navy lg:text-4xl">
                Les décryptages publiés les plus récents
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/veille" data-testid="signals-see-all" className="bz-link-underline inline-flex items-center gap-2 text-sm text-bz-navy">
                Voir tous les décryptages <ArrowRight size={16} strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>

          {status === "loading" ? (
            <p className="mt-16 text-bz-slate" data-testid="signals-loading">Chargement des décryptages…</p>
          ) : status === "error" ? (
            <div className="mt-16" data-testid="signals-error">
              <p className="font-serif text-xl leading-snug text-bz-navy">Les décryptages n'ont pas pu être chargés.</p>
              <p className="mt-3 text-bz-slate">Une erreur technique est survenue. Le contenu existe : il s'agit d'un problème de chargement, pas d'une absence de décryptage.</p>
              <button
                onClick={loadRecent}
                data-testid="signals-retry"
                className="mt-8 inline-flex border border-bz-navy px-6 py-3 text-xs uppercase tracking-[0.15em] text-bz-navy transition-colors hover:bg-bz-navy hover:text-bz-ivory"
              >
                Réessayer
              </button>
            </div>
          ) : recent.length === 0 ? (
            <p className="mt-16 text-bz-slate" data-testid="signals-empty">Aucun décryptage disponible pour le moment.</p>
          ) : (
            <div className="mt-16 grid grid-cols-1 gap-10 md:grid-cols-3">
              {recent.map((a, i) => (
                <Reveal key={a.slug} delay={i * 0.1}>
                  <Link
                    to={`/veille/${a.slug}`}
                    data-testid={`signal-card-${i}`}
                    className="group flex h-full flex-col border border-bz-line bg-white transition-colors duration-300 hover:border-bz-petrol"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={a.image || "/articles/placeholder.webp"}
                        alt={a.image_alt || "Illustration éditoriale BENZENATI"}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-1 flex-col p-8">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="border border-bz-line px-3 py-1 text-[0.6rem] uppercase tracking-[0.12em] text-bz-slate">{categoryShort(a.category)}</span>
                        <span className={`border px-3 py-1 text-[0.6rem] uppercase tracking-[0.12em] ${importanceClass(a.importance)}`}>{importanceLabel(a.importance)}</span>
                      </div>
                      <p className="mt-4 text-xs text-bz-slate/70">{formatDate(a.published_at)}</p>
                      <h3 className="mt-2 font-serif text-2xl leading-snug text-bz-navy">{a.title}</h3>
                      <p className="mt-4 flex-1 text-base leading-relaxed text-bz-slate">{a.summary}</p>
                      <span className="mt-6 inline-flex items-center gap-2 text-sm text-bz-navy">
                        Lire le décryptage <ArrowRight size={15} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* LA MÉTHODE BENZENATI */}
      <section className="border-b border-bz-line bg-bz-ivory2" data-testid="home-method">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <SectionLabel>La méthode BENZENATI</SectionLabel>
                <p className="mt-8 text-lg leading-relaxed text-bz-slate">
                  Notre démarche ne consiste pas à relayer toute l'actualité. Elle vise à sélectionner les évolutions qui peuvent réellement modifier la manière de décider, d'organiser ou de développer une PME.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 gap-px overflow-hidden border border-bz-line bg-bz-line sm:grid-cols-2">
                {method.map((m, i) => (
                  <Reveal key={m.title} delay={i * 0.08} className="bg-white">
                    <div className="flex h-full items-start gap-5 p-8" data-testid={`method-step-${i}`}>
                      <span className="font-serif text-2xl text-bz-amber">{m.n}</span>
                      <div>
                        <m.icon size={24} strokeWidth={1.2} className="text-bz-petrol" />
                        <h3 className="mt-4 font-serif text-xl text-bz-navy">{m.title}</h3>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* POURQUOI CET ESPACE */}
      <section className="border-b border-bz-line" data-testid="home-why">
        <div className="mx-auto max-w-4xl px-6 py-24 lg:px-10 lg:py-32">
          <Reveal>
            <SectionLabel>Pourquoi cet espace</SectionLabel>
            <p className="mt-10 font-serif text-2xl leading-snug text-bz-navy lg:text-3xl">
              BENZENATI est né d'une expérience entrepreneuriale de terrain et d'une conviction : une information n'a de valeur que lorsqu'elle devient compréhensible, contextualisée et exploitable.
            </p>
            <p className="mt-8 text-lg leading-relaxed text-bz-slate">
              Cet espace a vocation à réunir progressivement des analyses, des outils, des regards complémentaires et des échanges avec des professionnels capables d'éclairer les transformations des PME.
            </p>
          </Reveal>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="bg-bz-navy" data-testid="home-newsletter">
        <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-8">
              <Reveal>
                <div className="flex items-center gap-4">
                  <span className="h-px w-10 bg-bz-amber" />
                  <span className="bz-label text-white/50">La lettre BENZENATI</span>
                </div>
                <h2 className="mt-8 max-w-2xl font-serif text-3xl leading-tight text-bz-ivory lg:text-5xl">
                  Uniquement des sujets qui méritent d'être compris
                </h2>
                <p className="mt-8 max-w-xl text-lg leading-relaxed text-white/70">
                  Une sélection périodique de décryptages, outils et points de vigilance utiles aux dirigeants de PME. Pas de bruit supplémentaire : uniquement des sujets qui méritent d'être compris.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Reveal delay={0.1}>
                <Link
                  to="/newsletter"
                  data-testid="home-newsletter-button"
                  className="inline-flex items-center justify-center bg-bz-amber px-8 py-4 text-xs uppercase tracking-[0.18em] text-bz-navy transition-colors duration-300 hover:bg-bz-ivory"
                >
                  S'inscrire à la lettre
                </Link>
                <p className="mt-6 text-xs uppercase tracking-[0.15em] text-white/40">
                  Inscription libre · désinscription possible à tout moment.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
